import {gql, QueryOptions} from '@apollo/client';
import Backbone from 'backbone';

import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ApolloFileState from 'filestate/ApolloFileState';
import {FileStateAttributes} from 'filestate/FileState';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {agentFactoryPageUrl} from 'llm/routes/UrlUtils';
import * as navigation from 'Navigation';

import {
  AgentFactoryFileStateQuery,
  AgentFactoryFileStateQueryVariables,
} from './__generated/AgentFactoryFileStateQuery';
import AgentFactoryTabState from './AgentFactoryTabState';

export const AGENT_FACTORY_FILE_STATE_QUERY = gql`
  query AgentFactoryFileStateQuery($agentFactoryId: StrictString!) @skipAudit @engComponent(name: LLM) {
    agentFactory(id: $agentFactoryId) {
      ... on AgentFactoryNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface AgentFactoryFileStateAttributes {
  name?: string;
  hideTabNav?: boolean;
}

class AgentFactoryFileState extends ApolloFileState<
  AgentFactoryFileStateQuery,
  AgentFactoryFileStateQueryVariables,
  AgentFactoryFileStateAttributes
> {
  static fileType = ModelTypeConstants.AGENT_FACTORY;

  static override getQueryOptions(fileId: string): QueryOptions<AgentFactoryFileStateQueryVariables> {
    return {
      query: AGENT_FACTORY_FILE_STATE_QUERY,
      variables: {agentFactoryId: fileId},
    };
  }

  static override dataToAttributes(data: AgentFactoryFileStateQuery): AgentFactoryFileStateAttributes {
    const {agentFactory} = data;
    return agentFactory == null || isInaccessible(agentFactory) ? {} : {name: agentFactory.name};
  }

  static override openFile(fileId: string): JQueryPromise<AgentFactoryFileState> {
    return super.openFile(fileId) as JQueryPromise<AgentFactoryFileState>;
  }

  agentFactoryTab!: AgentFactoryTabState;

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.SingleBufferStatusBarState(),
      hideTabNav: true,
    };
  }

  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  override initialize(...args: any): void {
    super.initialize(...args);

    this.agentFactoryTab = new AgentFactoryTabState({
      agentFactoryId: this.id,
    });

    const buffers = new navigation.BufferStates([
      new navigation.BufferState(),
      new navigation.BufferState({active: false}),
    ]);
    this.set({buffers});
    const buffer = buffers.at(0);
    buffer.tabs().add(this.agentFactoryTab);
    buffer.tabs().at(0).set({active: true});
  }

  override editURL(): string {
    return agentFactoryPageUrl(this.id);
  }

  override name(): string {
    return this.get('name') ?? 'Loading...';
  }

  override registerTitleAndURLSync(): void {
    this.on('change:name', () => {
      this.syncTitleAndURL();
    });
  }
}

export default AgentFactoryFileState;
